import React, { FC } from "react";
import {
  BarFill,
  BarTrack,
  StepperCounter,
  StepperSegment,
  StepperTrack,
  StepperWrapper,
  V3ProgressBarTone,
} from "./Stepper.styled";

export type { V3ProgressBarTone };

export interface V3StepperProps {
  current: number;
  total: number;
  /** Optional per-step names; the current one is appended to the counter. */
  labels?: string[];
  testID?: string;
}

export const V3Stepper: FC<V3StepperProps> = ({
  current,
  total,
  labels,
  testID,
}) => {
  const steps = Math.max(1, Math.floor(total));
  const step = Math.min(Math.max(1, Math.floor(current)), steps);
  const stepLabel = labels?.[step - 1];
  const counter = `Step ${step} of ${steps}`;

  return (
    <StepperWrapper data-testid={testID}>
      <StepperTrack
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={steps}
        aria-valuenow={step}
        aria-valuetext={stepLabel ? `${counter}, ${stepLabel}` : counter}
      >
        {Array.from({ length: steps }, (_, index) => (
          <StepperSegment key={index} $filled={index < step} />
        ))}
      </StepperTrack>
      <StepperCounter>
        {stepLabel ? `${counter} · ${stepLabel}` : counter}
      </StepperCounter>
    </StepperWrapper>
  );
};

export interface V3ProgressBarProps {
  value: number;
  max: number;
  tone?: V3ProgressBarTone;
  /** Accessible name, e.g. "Fundraising progress". */
  label?: string;
  testID?: string;
}

export const V3ProgressBar: FC<V3ProgressBarProps> = ({
  value,
  max,
  tone = "brand",
  label,
  testID,
}) => {
  const ceiling = max > 0 ? max : 1;
  const filled = Math.min(Math.max(value, 0), ceiling);

  return (
    <BarTrack
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={ceiling}
      aria-valuenow={filled}
      aria-label={label}
      data-testid={testID}
    >
      <BarFill
        $tone={tone}
        style={{ width: `${(filled / ceiling) * 100}%` }}
        data-testid={testID ? `${testID}-fill` : undefined}
      />
    </BarTrack>
  );
};
